import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/auth';
import { hasSuperAdminAccess, hasOrgAdminAccess } from '@/context/auth/types';
import CampaignsTab from './tabs/CampaignsTab';
import TemplatesTab from './tabs/TemplatesTab';
import SubscribersTab from './tabs/SubscribersTab';
import TriggersTab from './tabs/TriggersTab';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useSearchParams } from 'react-router-dom';

interface EmailDashboardProps {
  initialTab?: string;
}

const EmailDashboard: React.FC<EmailDashboardProps> = ({ initialTab }) => {
  const { user } = useAuth();
  const organisationId = user?.organisationId;

  // Use search params for tab state
  const [searchParams, setSearchParams] = useSearchParams();

  // Get the active tab from URL parameters or use initial/default
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(initialTab || tabFromUrl || 'campaigns');

  const [tabError, setTabError] = useState<string | null>(null);

  // Update active tab when URL parameters change
  useEffect(() => {
    if (tabFromUrl) {
      if (tabFromUrl !== activeTab) {
        setActiveTab(tabFromUrl);
      }
    }
  }, [tabFromUrl]);

  // Update active tab when initialTab prop changes
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
      // Update the URL to match the initialTab
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', initialTab);
      setSearchParams(newSearchParams);
    }
  }, [initialTab, setSearchParams]);

  // Determine if user can access management features based on role
  const canAccessManagement = user && (
    hasSuperAdminAccess(user.role) ||
    hasOrgAdminAccess(user.role)
  );

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Update URL with new tab
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', value);
    setSearchParams(newSearchParams);
    // Reset error state when changing tabs
    setTabError(null);
  };

  const handleTabError = (error: Error) => {
    setTabError(error.message || 'An error occurred while loading this tab');
  };

  const handleRetry = () => {
    setTabError(null);
  };

  return (
    <div className="space-y-6 min-h-[calc(100vh+1px)] w-full overflow-hidden">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
            <div>
              <CardTitle>Email Marketing Dashboard</CardTitle>
              <CardDescription>
                Manage your email campaigns, templates, subscribers, and automated triggers
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap h-auto w-full mb-4">
              <TabsTrigger value="campaigns" className="sm:flex-[1_0_21%] lg:flex-[1_0_13%]">Campaigns</TabsTrigger>
              <TabsTrigger value="templates" className="sm:flex-[1_0_21%] lg:flex-[1_0_13%]">Templates</TabsTrigger>
              <TabsTrigger value="subscribers" className="sm:flex-[1_0_21%] lg:flex-[1_0_13%]">Subscribers</TabsTrigger>
              <TabsTrigger value="triggers" className="sm:flex-[1_0_21%] lg:flex-[1_0_13%]">Triggers</TabsTrigger>
            </TabsList>

            {tabError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription className="flex flex-col gap-2">
                  <p>{tabError}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetry}
                    className="w-fit"
                  >
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <TabsContent value="campaigns">
              <CampaignsTab onError={handleTabError} />
            </TabsContent>

            <TabsContent value="templates">
              <TemplatesTab onError={handleTabError} />
            </TabsContent>

            <TabsContent value="subscribers">
              <SubscribersTab onError={handleTabError} />
            </TabsContent>

            <TabsContent value="triggers">
              <TriggersTab onError={handleTabError} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailDashboard;
